import { BffWorkingHours } from '@/graphql/__generated/sdk';
import { DAYS_OF_WEEK } from '@/constants';

export const checkIfPastCurrentTimeInMinutes = (officeTimeInMinute: string, localTimeInMinute: string) => {
	// taking only time - numeric from string like 00am
	const officeMinuteWithAmorPM = officeTimeInMinute.match(/\D+|\d+/g);
	const officeMinute = officeMinuteWithAmorPM ? officeMinuteWithAmorPM[0] : '00';
	return officeMinute <= localTimeInMinute;
};

export const isOfficeOpen = (todayWorkingHours: BffWorkingHours[], localTime: string, localAmOrPm: string) => {
	// Office is closed if we don't get working hours for today
	if (
		!todayWorkingHours?.length ||
		todayWorkingHours[0]?.open === null ||
		todayWorkingHours[0]?.open?.from === null
	) {
		return false;
	}

	const [localHour, localMinute] = localTime.split(':');

	//setting 12 as 0 since its the start for am or pm
	const localHourUpdated = localHour === '12' ? '00' : localHour;

	if (localAmOrPm === 'AM') {
		const officeFromTime = todayWorkingHours[0].open?.from as string;

		//check the hour first
		const [officeAmHour, officeAmMinute] = officeFromTime.split(':');
		if (Math.abs(officeAmHour as unknown as number) < Math.abs(localHourUpdated as unknown as number)) {
			return true;
		} else if (Math.abs(officeAmHour as unknown as number) === Math.abs(localHourUpdated as unknown as number)) {
			return checkIfPastCurrentTimeInMinutes(officeAmMinute, localMinute);
		}
	}

	if (localAmOrPm === 'PM') {
		const officeToTime = todayWorkingHours[0].open?.to as string;

		//check the hour first
		const [officePmHour, officePmMinute] = officeToTime.split(':');
		if (Math.abs(officePmHour as unknown as number) > Math.abs(localHourUpdated as unknown as number)) {
			return true;
		} else if (Math.abs(officePmHour as unknown as number) === Math.abs(localHourUpdated as unknown as number)) {
			// reverse check because office is open till that time
			return !checkIfPastCurrentTimeInMinutes(officePmMinute, localMinute);
		}
	}
	return false;
};

export const getTodayCloseTime = (todayWorkingHours: BffWorkingHours[]) => {
	return todayWorkingHours[0]?.open?.to ?? '';
};

export const getNextOpenTime = (todayDate: string, bffWorkingHours: BffWorkingHours[], localAmOrPm: string) => {
	// If there are no working hours at all, return empty string
	if (bffWorkingHours.length === 0) {
		return '';
	}

	// If we couldn't determine today's day, try to find any open day
	if (!todayDate) {
		const anyOpenDay = bffWorkingHours.find((hours) => hours?.open?.from !== null);
		return anyOpenDay ? `${anyOpenDay.open?.from as string} ${anyOpenDay.date as string}` : '';
	}

	// Find the index of today in the days of week array
	const todayDayIndex = DAYS_OF_WEEK.indexOf(todayDate);

	let nextOpenDay: BffWorkingHours | undefined;

	// If it's AM, check from today onwards
	// If it's PM, check from tomorrow onwards
	const startDayIndex = localAmOrPm === 'AM' ? todayDayIndex : todayDayIndex + 1;

	// First check from today/tomorrow to end of week
	for (let i = startDayIndex; i < DAYS_OF_WEEK.length; i++) {
		const dayName = DAYS_OF_WEEK[i];
		const dayHours = bffWorkingHours.find((hours) => hours.date === dayName && hours?.open?.from !== null);

		if (dayHours) {
			nextOpenDay = dayHours;
			break;
		}
	}

	// If no open day found after today, check from beginning of week to today
	if (!nextOpenDay) {
		const endDayIndex = localAmOrPm === 'AM' ? todayDayIndex - 1 : todayDayIndex;

		for (let i = 0; i <= endDayIndex; i++) {
			const dayName = DAYS_OF_WEEK[i];
			const dayHours = bffWorkingHours.find((hours) => hours.date === dayName && hours?.open?.from !== null);

			if (dayHours) {
				nextOpenDay = dayHours;
				break;
			}
		}
	}

	return nextOpenDay ? `${nextOpenDay.open?.from as string} ${nextOpenDay.date as string}` : '';
};
